import React, { useMemo } from 'react';
import { Alert, Box, Button, Stack, Typography, LinearProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';

/**
 * Post-approval onboarding checklist for riders.
 */
const FEATURE_TIPS = [
  'Pick up hauls in Contract Hub — track active jobs and claim payouts when you deliver.',
  'Top up tokens in your Wallet and keep your driver pass active to stay on the road.',
  'Join a Division to roll with a fleet, share tax boards, and climb together.',
  'Mark attendance at convoy events to build streaks and unlock bonus rewards.',
  'Take on Challenges for extra XP and climb the company leaderboard.',
  'Browse the Calendar for upcoming convoy slots and book your seat early.',
  'Manage trucks in Fleet — buy, upgrade, and keep your garage ready for long hauls.',
  'Set Personal Goals and track distance, jobs, and income from your dashboard.',
  'Need a boost? Apply for a Loan to fund your next truck or pass renewal.',
  'Check Results for a live snapshot of jobs, revenue, and distance driven.',
];

export default function RiderOnboardingChecklist({
  data,
  divisionSummary,
  invitesCount = 0,
}) {
  const rider = data?.rider || {};
  const driverPass = data?.driverPass || {};
  const attendance = data?.attendance || {};
  const randomTip = useMemo(
    () => FEATURE_TIPS[Math.floor(Math.random() * FEATURE_TIPS.length)],
    []
  );
  const steps = useMemo(() => ([
    {
      id: 'steam-tmp',
      label: 'Link Steam / TruckersMP / TruckersHub IDs',
      done: Boolean(rider.steamID || rider.steamId || rider.steamID64 || rider.truckersmpId || rider.truckershubId),
      to: '/profile',
    },
    {
      id: 'pass',
      label: 'Buy or activate a driver pass',
      done: Boolean(driverPass.valid),
      to: '/wallet',
    },
    {
      id: 'division',
      label: invitesCount > 0 ? 'Respond to division invite' : 'Join a division',
      done: Boolean(divisionSummary?.division?._id || rider.divisionId),
      to: invitesCount > 0 ? '/division/invites' : '/divisions',
    },
    {
      id: 'attendance',
      label: 'Mark your first attendance',
      done: Number(
        attendance.totalEventsAttended
        ?? attendance.total
        ?? attendance.count
        ?? (Array.isArray(attendance.eventsAttended) ? attendance.eventsAttended.length : 0)
        ?? 0
      ) > 0,
      to: '/attendance/me',
    },
  ]), [rider, driverPass, divisionSummary, invitesCount, attendance]);

  const remaining = steps.filter((s) => !s.done);
  // Hide entirely once every onboarding step is complete
  if (remaining.length === 0) return null;

  const doneCount = 4;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <>
    {doneCount !== 4 && (
    <Alert
      severity="info"
      sx={{ mb: 2, alignItems: 'flex-start' }}
      icon={false}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Getting started ({doneCount}/{steps.length})
      </Typography>
      <LinearProgress variant="determinate" value={pct} sx={{ mb: 1.5, borderRadius: 1 }} />
      <Stack spacing={1}>
        {steps.map((step) => (
          <Stack key={step.id} direction="row" spacing={1} alignItems="center">
            {step.done
              ? <CheckCircleOutline color="success" fontSize="small" />
              : <RadioButtonUnchecked color="disabled" fontSize="small" />}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ textDecoration: step.done ? 'line-through' : 'none', color: step.done ? 'text.secondary' : 'text.primary' }}
              >
                {step.label}
              </Typography>
            </Box>
            {!step.done && (
              <Button component={RouterLink} to={step.to} size="small">
                Go
              </Button>
            )}
          </Stack>
        ))}
      </Stack>
    </Alert>
    )}
    {doneCount === 4 && (
     <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      {randomTip}
     </Typography>
    )}
    </>
  );
}
